import APIClient from '../../../lib/api';

class ServiceService {
  async getByClient(clientId, params = {}) {
    return this.getAll({ client_id: clientId, ...params });
  }

  async getAll(params = {}) {
    const defaultParams = {
      skip: 0,
      limit: 50,
    };
    
    const queryParams = { ...defaultParams, ...params };
    
    try {
      const response = await APIClient.services.getAll(queryParams);
      return {
        success: true,
        data: response.data.data || response.data,
        total: response.data.total || response.data.length
      };
    } catch (error) {
      console.error('ServiceService.getAll Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα φόρτωσης υπηρεσιών'
      };
    }
  }

  async getById(serviceId) {
    try {
      const response = await APIClient.services.getById(serviceId);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('ServiceService.getById Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα φόρτωσης υπηρεσίας'
      };
    }
  }

  async create(serviceData) {
    try {
      // Clean up empty date fields for one-time services
      const cleanedData = { ...serviceData };
      if (cleanedData.billing_period === 'one_time') {
        // For one-time services, set empty date fields to null
        if (!cleanedData.end_date || cleanedData.end_date.trim() === '') {
          cleanedData.end_date = null;
        }
        if (!cleanedData.next_billing_date || cleanedData.next_billing_date.trim() === '') {
          cleanedData.next_billing_date = null;
        }
      }
      
      const response = await APIClient.services.create(cleanedData);
      return {
        success: true,
        data: response.data,
        message: 'Η υπηρεσία δημιουργήθηκε επιτυχώς'
      };
    } catch (error) {
      console.error('ServiceService.create Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα δημιουργίας υπηρεσίας'
      };
    }
  }

  async update(serviceId, updateData) {
    try {
      // Clean up empty date fields for one-time services
      const cleanedData = { ...updateData };
      if (cleanedData.billing_period === 'one_time') {
        // For one-time services, set empty date fields to null
        if (!cleanedData.end_date || cleanedData.end_date.trim() === '') {
          cleanedData.end_date = null;
        }
        if (!cleanedData.next_billing_date || cleanedData.next_billing_date.trim() === '') {
          cleanedData.next_billing_date = null;
        }
      }
      
      const response = await APIClient.services.update(serviceId, cleanedData);
      return {
        success: true,
        data: response.data,
        message: 'Η υπηρεσία ενημερώθηκε επιτυχώς'
      };
    } catch (error) {
      console.error('ServiceService.update Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα ενημέρωσης υπηρεσίας'
      };
    }
  }

  async delete(serviceId) {
    try {
      await APIClient.services.delete(serviceId);
      return {
        success: true,
        message: 'Η υπηρεσία διαγράφηκε επιτυχώς'
      };
    } catch (error) {
      console.error('ServiceService.delete Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα διαγραφής υπηρεσίας'
      };
    }
  }

  async getPaymentSummary(serviceId) {
    try {
      const response = await APIClient.services.getPaymentSummary(serviceId);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('ServiceService.getPaymentSummary Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα φόρτωσης πληροφοριών πληρωμής'
      };
    }
  }

  async getClientServicesPaymentSummary(clientId) {
    try {
      const response = await APIClient.services.getClientServicesPaymentSummary(clientId);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('ServiceService.getClientServicesPaymentSummary Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα φόρτωσης πληροφοριών πληρωμών πελάτη'
      };
    }
  }

  async getStatistics(params = {}) {
    try {
      const response = await APIClient.services.getStatistics(params);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('ServiceService.getStatistics Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα φόρτωσης στατιστικών υπηρεσιών'
      };
    }
  }

  validate(serviceData) {
    const errors = {};

    if (!serviceData.name || serviceData.name.trim() === '') {
      errors.name = 'Το όνομα υπηρεσίας είναι υποχρεωτικό';
    }

    if (!serviceData.service_type) {
      errors.service_type = 'Ο τύπος υπηρεσίας είναι υποχρεωτικός';
    }

    if (!serviceData.price || serviceData.price < 0) {
      errors.price = 'Η τιμή πρέπει να είναι μεγαλύτερη ή ίση με 0';
    }

    if (!serviceData.client_id) {
      errors.client_id = 'Ο πελάτης είναι υποχρεωτικός';
    }

    if (!serviceData.quantity || serviceData.quantity < 1) {
      errors.quantity = 'Η ποσότητα πρέπει να είναι τουλάχιστον 1';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Helper methods for form data
  formatServiceType(type) {
    const types = {
      'development': 'Ανάπτυξη',
      'hosting': 'Hosting',
      'maintenance': 'Συντήρηση',
      'seo': 'SEO',
      'design': 'Σχεδιασμός',
      'other': 'Άλλο'
    };
    return types[type] || type;
  }

  formatBillingPeriod(period) {
    const periods = {
      'one_time': 'Μία φορά',
      'monthly': 'Μηνιαία',
      'quarterly': 'Τριμηνιαία',
      'semi_annual': 'Εξαμηνιαία',
      'annual': 'Ετήσια',
      'biennial': 'Διετής'
    };
    return periods[period] || period;
  }

  formatStatus(status) {
    const statuses = {
      'active': 'Active',
      'pending': 'Pending',
      'expired': 'Expired',
      'cancelled': 'Cancelled'
    };
    return statuses[status] || status;
  }

  formatPaymentStatus(status) {
    const statuses = {
      'unpaid': 'Ανεξόφλητη',
      'partial': 'Μερικώς εξοφλημένη',
      'paid': 'Εξοφλημένη'
    };
    return statuses[status] || status;
  }

  // Helper method to calculate next billing date
  calculateNextBillingDate(billingPeriod, currentEndDate) {
    const endDate = new Date(currentEndDate);
    
    switch (billingPeriod) {
      case 'monthly':
        endDate.setMonth(endDate.getMonth() + 1);
        break;
      case 'quarterly':
        endDate.setMonth(endDate.getMonth() + 3);
        break;
      case 'semi_annual':
        endDate.setMonth(endDate.getMonth() + 6);
        break;
      case 'annual':
        endDate.setFullYear(endDate.getFullYear() + 1);
        break;
      case 'biennial':
        endDate.setFullYear(endDate.getFullYear() + 2);
        break;
      default:
        return null;
    }
    
    return endDate.toISOString().split('T')[0];
  }

  // Helper method to calculate end date from start date and billing period
  calculateEndDate(billingPeriod, startDate) {
    const start = new Date(startDate);
    const endDate = new Date(start);
    
    switch (billingPeriod) {
      case 'monthly':
        endDate.setMonth(endDate.getMonth() + 1);
        break;
      case 'quarterly':
        endDate.setMonth(endDate.getMonth() + 3);
        break;
      case 'semi_annual':
        endDate.setMonth(endDate.getMonth() + 6);
        break;
      case 'annual':
        endDate.setFullYear(endDate.getFullYear() + 1);
        break;
      case 'biennial':
        endDate.setFullYear(endDate.getFullYear() + 2);
        break;
      default:
        return null;
    }
    
    return endDate.toISOString().split('T')[0];
  }

  // Helper method to get billing status description
  getBillingStatusDescription(service) {
    if (service.billing_period === 'one_time') {
      return 'Εφάπαξ υπηρεσία - Χρέωση κατά την παράδοση';
    }
    
    const today = new Date();
    const nextBilling = new Date(service.next_billing_date);
    const endDate = new Date(service.end_date);
    
    if (today > endDate) {
      return 'Η συνδρομή έχει λήξει';
    } else if (today >= nextBilling) {
      return 'Επόμενη χρέωση έτοιμη';
    } else {
      const daysUntilBilling = Math.ceil((nextBilling - today) / (1000 * 60 * 60 * 24));
      return `Επόμενη χρέωση σε ${daysUntilBilling} ημέρες`;
    }
  }

  // Auto-renewal logic
  async processAutoRenewal(serviceId) {
    try {
      const serviceResult = await this.getById(serviceId);
      if (!serviceResult.success || !serviceResult.data.auto_renew) {
        return { 
          success: false, 
          message: 'Η υπηρεσία δεν έχει ενεργοποιημένη την αυτόματη ανανέωση' 
        };
      }
      
      const currentService = serviceResult.data;
      
      // Check if service is subscription type
      if (currentService.billing_period === 'one_time') {
        return { 
          success: false, 
          message: 'Οι εφάπαξ υπηρεσίες δεν μπορούν να ανανεωθούν αυτόματα' 
        };
      }
      
      const newEndDate = this.calculateNextBillingDate(
        currentService.billing_period, 
        currentService.end_date
      );
      
      const renewalData = {
        name: currentService.name,
        description: currentService.description,
        service_type: currentService.service_type,
        price: currentService.price,
        quantity: currentService.quantity,
        billing_period: currentService.billing_period,
        vat_rate: currentService.vat_rate,
        client_id: currentService.client_id,
        project_id: currentService.project_id,
        start_date: currentService.end_date, // Νέα υπηρεσία ξεκινάει όταν λήγει η παλιά
        end_date: newEndDate,
        next_billing_date: newEndDate, // Η επόμενη χρέωση είναι στο τέλος της νέας περιόδου
        auto_renew: currentService.auto_renew,
        status: 'active',
        notes: `Αυτόματη ανανέωση από υπηρεσία #${currentService.id} - Νέα περίοδος: ${currentService.end_date} έως ${newEndDate}`
      };
      
      return await this.create(renewalData);
    } catch (error) {
      console.error('ServiceService.processAutoRenewal Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα αυτόματης ανανέωσης υπηρεσίας'
      };
    }
  }

  // Helper method to check if service is subscription
  isSubscription(service) {
    return Boolean(
      service?.billing_period &&
      service.billing_period !== 'one_time' &&
      service.start_date &&
      service.end_date
    );
  }

  // Scheduler methods
  async getUpcomingServices(daysAhead = 30) {
    try {
      const response = await APIClient.scheduler.getUpcomingServices(daysAhead);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('ServiceService.getUpcomingServices Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα φόρτωσης επερχόμενων υπηρεσιών'
      };
    }
  }

  async earlyRenewService(serviceId) {
    try {
      const response = await APIClient.scheduler.earlyRenew(serviceId);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('ServiceService.earlyRenewService Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα πρόωρης ανανέωσης'
      };
    }
  }

  async sendExpirationWarnings(serviceIds) {
    try {
      const response = await APIClient.scheduler.sendExpirationWarnings(serviceIds);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('ServiceService.sendExpirationWarnings Error:', error);
      const detail = error.response?.data?.detail;
      return {
        success: false,
        error: typeof detail === 'string' ? detail : error.message || 'Σφάλμα αποστολής email λήξης'
      };
    }
  }

  // Service Renewal methods (moved from serviceRenewalService)
  async getRenewals(params = {}) {
    try {
      const response = await APIClient.serviceRenewals.getAll(params);
      return {
        success: true,
        data: response.data.data || response.data,
        total: response.data.total || response.data.length
      };
    } catch (error) {
      console.error('ServiceService.getRenewals Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα φόρτωσης ανανεώσεων'
      };
    }
  }

  async getRenewalById(id) {
    try {
      const response = await APIClient.serviceRenewals.getById(id);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('ServiceService.getRenewalById Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα φόρτωσης ανανέωσης'
      };
    }
  }

  async cancelRenewal(id) {
    try {
      const response = await APIClient.serviceRenewals.cancel(id);
      return {
        success: true,
        message: 'Η ανανέωση ακυρώθηκε επιτυχώς'
      };
    } catch (error) {
      console.error('ServiceService.cancelRenewal Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα ακύρωσης ανανέωσης'
      };
    }
  }

  async processRenewal(id) {
    try {
      const response = await APIClient.serviceRenewals.process(id);
      return {
        success: true,
        message: 'Η ανανέωση επεξεργάστηκε επιτυχώς'
      };
    } catch (error) {
      console.error('ServiceService.processRenewal Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα επεξεργασίας ανανέωσης'
      };
    }
  }
}

const serviceService = new ServiceService();
export default serviceService;
